import React, { useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import EncodingProfileForm from "./EncodingProfileForm";
import EncodingProfileSummary from "./EncodingProfileSummary";
import EncoderAPI from "../../../api/EncoderAPI";
import { ENCODING_PROFILES_ROUTE } from "../../routes/routes";
import Breadcrumbs from "../../utils/Breadcrumbs";
import ContainerHeader from "../../utils/ContainerHeader";
import InfoPanel from "../../utils/InfoPanel";

export interface QualitiesCost {
  video: {
    "2160p": number;
    "1080p": number;
    "720p": number;
    "480p": number;
    "360p": number;
  };
  audio: {
    "320kbps": number;
    "192kbps": number;
    "128kbps": number;
  };
  fastAsset: number;
  downloadToGo: number;
}

export interface EncodingProfileFormValues {
  label: string;
  videoFormats: {
    "2160p": boolean;
    "1080p": boolean;
    "720p": boolean;
    "480p": boolean;
    "360p": boolean;
  };
  audioFormats: {
    "320kbps": boolean;
    "192kbps": boolean;
    "128kbps": boolean;
  };
  fast: boolean;
  downloadToGo: boolean;
  fastFramerate: string;
}

function EncodingProfileEdit(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation("editor-encoding-profile");
  const queryClient = useQueryClient();
  const history = useHistory();
  const isNew = id === "new";

  const getEncodingProfileListQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getEncodingProfileList,
    params: [],
  });

  const getEncodingProfileQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getEncodingProfile,
    params: [id],
    options: {
      enabled: !isNew,
    },
  });

  const { data: encodingProfile } = useQuery(getEncodingProfileQuery);

  const getQualitiesCostQuery = EncoderAPI.generateQuery({
    fn: EncoderAPI.getQualitiesCost,
    params: [],
  });

  const { data: qualitiesCostData } = useQuery(getQualitiesCostQuery);

  const updateEncodingProfileMutation = useMutation({
    mutationFn: async (data: EncodingProfileFormValues) => {
      const encodingProfileData = {
        label: data.label,
        ott_video_2160p: data.videoFormats["2160p"],
        ott_video_1080p: data.videoFormats["1080p"],
        ott_video_720p: data.videoFormats["720p"],
        ott_video_480p: data.videoFormats["480p"],
        ott_video_360p: data.videoFormats["360p"],
        ott_audio_320k: data.audioFormats["320kbps"],
        ott_audio_192k: data.audioFormats["192kbps"],
        ott_audio_128k: data.audioFormats["128kbps"],
        fast: data.fast,
        ott_download_to_go: data.downloadToGo,
        fast_framerate_target: Number(data.fastFramerate),
      };

      if (isNew) {
        return EncoderAPI.createEncodingProfile(encodingProfileData);
      }

      return EncoderAPI.updateEncodingProfile({
        uuid: id,
        data: encodingProfileData,
      });
    },
    onSuccess: (updatedEncodingProfile) => {
      const successMessage = isNew
        ? t("encodingProfileForm.createEncodingProfileSuccess")
        : t("encodingProfileForm.updateEncodingProfileSuccess");

      toast.success(successMessage);

      queryClient.invalidateQueries(getEncodingProfileListQuery.queryKey);
      queryClient.setQueryData(
        getEncodingProfileQuery.queryKey,
        updatedEncodingProfile,
      );

      if (isNew) {
        history.replace(
          `${ENCODING_PROFILES_ROUTE}/${updatedEncodingProfile.uuid}`,
        );
      }
    },
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const qualitiesCost = useMemo<QualitiesCost | null>(() => {
    if (!qualitiesCostData) return null;
    return {
      video: {
        "2160p": qualitiesCostData.ott_video_2160p,
        "1080p": qualitiesCostData.ott_video_1080p,
        "720p": qualitiesCostData.ott_video_720p,
        "480p": qualitiesCostData.ott_video_480p,
        "360p": qualitiesCostData.ott_video_360p,
      },
      audio: {
        "320kbps": qualitiesCostData.ott_audio_320k,
        "192kbps": qualitiesCostData.ott_audio_192k,
        "128kbps": qualitiesCostData.ott_audio_128k,
      },
      fastAsset: qualitiesCostData.fast,
      downloadToGo: qualitiesCostData.ott_download_to_go,
    };
  }, [qualitiesCostData]);

  const defaultFormValues: EncodingProfileFormValues = useMemo(
    () => ({
      label: "",
      videoFormats: {
        "2160p": false,
        "1080p": false,
        "720p": false,
        "480p": false,
        "360p": false,
      },
      audioFormats: {
        "320kbps": false,
        "192kbps": false,
        "128kbps": false,
      },
      fast: false,
      downloadToGo: false,
      fastFramerate: "",
    }),
    [],
  );

  const methods = useForm<EncodingProfileFormValues>({
    values:
      isNew || !encodingProfile
        ? defaultFormValues
        : {
            label: encodingProfile.label,
            videoFormats: {
              "2160p": encodingProfile.ott_video_2160p,
              "1080p": encodingProfile.ott_video_1080p,
              "720p": encodingProfile.ott_video_720p,
              "480p": encodingProfile.ott_video_480p,
              "360p": encodingProfile.ott_video_360p,
            },
            audioFormats: {
              "320kbps": encodingProfile.ott_audio_320k,
              "192kbps": encodingProfile.ott_audio_192k,
              "128kbps": encodingProfile.ott_audio_128k,
            },
            fast: encodingProfile.fast,
            downloadToGo: encodingProfile.ott_download_to_go,
            fastFramerate: encodingProfile.fast_framerate_target.toString(),
          },
  });

  const breadcrumbs = [
    {
      name: t("encodingProfiles"),
      url: ENCODING_PROFILES_ROUTE,
    },
    {
      name: isNew ? t("create") : t("edit"),
    },
  ];

  const handleFormSubmit = (data: EncodingProfileFormValues): void => {
    updateEncodingProfileMutation.mutate(data);
  };

  const isDefault = encodingProfile?.default ?? false;

  const defaultTagElement = isDefault ? (
    <div className="border-border-strong text-foreground mx-3 rounded-lg border px-2 py-0.5 text-[0.78125rem]">
      {t("encodingProfileForm.default")}
    </div>
  ) : undefined;

  const infoPanelLinks = {
    en: "https://support.okast.tv/en/support/solutions/articles/103000402637-encoding-profiles",
    fr: "https://support.okast.tv/fr/support/solutions/articles/103000402637-profils-d-encodage",
  };

  return (
    <div className="flex-1 overflow-x-hidden">
      <div className="w-full p-7.5">
        <div className="mx-auto max-w-300">
          <div className="flex">
            <Breadcrumbs items={breadcrumbs} />
            <InfoPanel links={infoPanelLinks} />
          </div>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <FormProvider {...methods}>
            <ContainerHeader
              withBackBtn
              customComponent={defaultTagElement}
              onSave={methods.handleSubmit(handleFormSubmit)}
              item={{
                ...encodingProfile,
                label: isNew
                  ? t("encodingProfileForm.encodingProfileNew")
                  : encodingProfile?.label,
              }}
            />
            <div className="flex flex-col-reverse gap-6.25 xl:flex-row">
              <EncodingProfileForm qualitiesCost={qualitiesCost} />
              <EncodingProfileSummary qualitiesCost={qualitiesCost} />
            </div>
          </FormProvider>
        </div>
      </div>
    </div>
  );
}

export default EncodingProfileEdit;
